/* eslint-disable max-len */
import { Col, Form, Row } from 'antd';
import React, { useContext, useEffect, useState } from 'react';
import { ILeiData, IZalozeniLeiInput } from 'types/leiTypes';

import ButtonComponent from 'components/atoms/ButtonComponent';
import ContentCard from 'components/atoms/ContentCard';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import FormWrapper from 'components/molecules/FormWrapper';
import InfoTooltip from 'components/molecules/InfoTooltip';
import Pruvodce from 'components/pages/Lei/formularPages/Pruvodce';
import RozcestnikZalozeni from 'components/pages/Lei/formularPages/RozcestnikZalozeni';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import emitentApi from 'services/emitent';
import checkIcApi from 'services/lei/checkIC';
import leiRejstrikApi from 'services/lei/leiRejstrik';
import zalozeniLeiApi from 'services/lei/zalozeniLei';
import nastavitUcetApi from 'services/nastavitUcet';
import novyUcetApi, { nazevSpolecnostiApi } from 'services/novyUcet';
import profilyApi from 'services/profily';
import { zadostPravoCheck } from 'services/spravaEmitenta/zadostPravo';

import { EEmitentJe } from 'enums/isin/emitentJe';
import { ETypZadostiLei } from 'enums/lei/leiDataEnums';
import routePathEnum from 'enums/routePathEnum';

import { leiRejstrikyPermanent, leiRejstrikySelect } from 'data/codebooks/lei/leiRejstrikySelect';

import { _getKeyValue_ } from 'utils/getObjectKeyValue';
import icoValidation from 'utils/validations/icoValidation';
import leiValidation from 'utils/validations/leiValidation';

import SpravaUctu from './Lei/SpravaUctu';

const NovyUcet: React.FC = () => {
    const [form] = Form.useForm();
    const { cisStaty, language, profily } = useContext(Context);
    const [loading, setLoading] = useState<boolean>(false);
    const { setLevel } = useProfily();
    const [apiError, setApiError] = useState<string>('');
    const [emitServiceResp, setEmitServiceResp] = useState<{ lei?: string; nazev: string }>();
    const [pruvodceData, setPruvodceData] = useState<IZalozeniLeiInput>();
    const [icoError, setIcoError] = useState<string>('');
    const [currentStep, setCurrentStep] = useState<number>(0);
    const [formData, setFormData] = useState<ILeiData | undefined>();
    const [isGovermentEntity, setIsGovermentEntity] = useState<boolean>(false);
    const { navigateWorkaround } = useNavigateWorkaround();
    const [rerenderValidate, setRerenderValidate] = useState<boolean>(false);
    const url = window.location.href.split('/');
    const [typUctu] = useState<'EMITENT' | 'SPRAVCELEI' | 'ADMEMIT' | 'PODLT'>(
        url[url.length - 1] as 'EMITENT' | 'SPRAVCELEI' | 'ADMEMIT' | 'PODLT'
    );
    const [typSpravceLei, setTypSpravceLei] = useState<'NOVE' | 'PRIDELENI'>();
    const [, rerender] = useState<boolean>(false);

    const getNazevEmitent = () => {
        form.validateFields().then(() => {
            setApiError('');
            setLoading(true);
            const { identifikator, stat } = form.getFieldsValue(['identifikator', 'stat']);
            if (identifikator === '00006947' && !profily?.email.includes('@gov.mf.cz')) {
                setLoading(false);
                setApiError('LEI_ICO_CHECK_ERR_GOV');
            } else {
                checkIcApi({
                    jurisdikce: stat,
                    registrationauthorityentityid: identifikator,
                    registrationauthorityid: 'RA888888',
                    typzadosti: ETypZadostiLei.BRANCH
                })
                    .then(data => {
                        if (data.lei) {
                            form.setFieldsValue({
                                nazev: data.spolecnost,
                                lei: data.lei
                            });
                            setEmitServiceResp({
                                nazev: data.spolecnost,
                                lei: data.lei
                            });
                        } else if (data.stav === 'NEEXISTUJE' || !data.spolecnost) {
                            if (stat === 'CZ') {
                                nazevSpolecnostiApi(identifikator)
                                    .then(resp => {
                                        setEmitServiceResp({
                                            nazev: resp.nazev || ''
                                        });
                                        form.setFieldsValue({
                                            nazev: data.spolecnost || resp.nazev
                                        });
                                    })
                                    .catch(() => {
                                        setEmitServiceResp({
                                            nazev: ''
                                        });
                                        setLoading(false);
                                    })
                                    .finally(() => setLoading(false));
                            } else {
                                typUctu !== 'PODLT' && setApiError('LEI_CHECK_ERROR2');
                                form.setFieldsValue({
                                    lei: undefined,
                                    nazev: undefined
                                });
                                setEmitServiceResp(
                                    typUctu === 'PODLT'
                                        ? {
                                              lei: '',
                                              nazev: ''
                                          }
                                        : undefined
                                );
                            }
                        }
                    })
                    .catch(() => {
                        form.setFieldsValue({
                            lei: undefined,
                            nazev: undefined
                        });
                        setEmitServiceResp(undefined);
                    })
                    .finally(() => setLoading(false));
            }
        });
    };

    const next = () => {
        setCurrentStep(currentStep + 1);
        window.scrollTo(0, 0);
    };
    const prev = () => {
        setCurrentStep(currentStep - 1);
        window.scrollTo(0, 0);
    };

    useEffect(() => {
        if (rerenderValidate) {
            form.validateFields(['registrationauthorityentityid']);
            setRerenderValidate(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rerenderValidate]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (values: any) => {
        setLoading(true);
        const zalozeniData = {
            ...(values.jurisdikce && {
                jurisdikce: values.jurisdikce
            }),
            ...(values.registrationauthorityid && {
                registrationauthorityid: values.registrationauthorityid
            }),
            ...(values.otherregistrationauthorityid && {
                otherregistrationauthorityid: values.otherregistrationauthorityid
            }),
            ...(values.registrationauthorityentityid && {
                registrationauthorityentityid: values.registrationauthorityentityid
            }),
            ...(values.legalname && {
                legalname: values.legalname
            }),
            ...(formData?.subcategory && {
                subcategory: formData?.subcategory
            }),
            typzadosti: formData?.typzadosti
        };
        if (typUctu === 'SPRAVCELEI') {
            checkIcApi(zalozeniData)
                .then(resp => {
                    if (resp.stav !== 'EXISTUJE' && resp.stav !== 'DUPLICITA') {
                        novyUcetApi({
                            identifikator: values.registrationauthorityentityid,
                            stat: values.jurisdikce,
                            nazev: values.legalname,
                            typ: 'SPRAVCELEI'
                        }).then(ucetResp => {
                            profilyApi()
                                .then(({ data }) => {
                                    const novyUcet = data.sezpristupu!.filter(e => e.iducet === ucetResp.iductu)[0];
                                    setLevel('profily', data);
                                    setLevel('ucty', data.sezpristupu!);
                                    setLevel('aktivniFirma', novyUcet);
                                    nastavitUcetApi(novyUcet.iducet).then(() => {
                                        if (
                                            (values.jurisdikce === 'CZ' || values.jurisdikce === 'SK') &&
                                            values.maid !== 'ne'
                                        ) {
                                            leiRejstrikApi({
                                                stat: values.jurisdikce,
                                                lang: 'cz',
                                                ic: values.registrationauthorityentityid,
                                                regauthor: values.registrationauthorityid
                                            })
                                                .then(respData => {
                                                    zalozeniLeiApi(zalozeniData).then(zalozeniResp => {
                                                        const dataResp = {
                                                            ...respData
                                                        };
                                                        delete dataResp.entitycheck;
                                                        setLevel('lei', {
                                                            data: {
                                                                typzadosti: formData?.typzadosti,
                                                                ...(formData?.subcategory && {
                                                                    subcategory: formData?.subcategory
                                                                }),
                                                                entitycheck: respData.entitycheck || 'N/A',
                                                                language: language,
                                                                spolecnost: {
                                                                    ...dataResp,
                                                                    jurisdikce:
                                                                        dataResp.jurisdikce || values.jurisdikce,
                                                                    registrationauthorityid:
                                                                        values.registrationauthorityid,
                                                                    registrationauthorityentityid:
                                                                        values.registrationauthorityentityid,
                                                                    ...(values.otherregistrationauthorityid && {
                                                                        otherregistrationauthorityid:
                                                                            values.otherregistrationauthorityid
                                                                    }),
                                                                    adresy: [
                                                                        {
                                                                            typadresy: 'LEGALADDRESS',
                                                                            country: values.jurisdikce,
                                                                            ...respData.adresy[0]
                                                                        }
                                                                    ]
                                                                }
                                                            },
                                                            idVarsymZadosti: zalozeniResp.data
                                                        });
                                                        navigateWorkaround(
                                                            `/firma/${ucetResp.iductu}${routePathEnum.LEI_NOVY}`
                                                        );
                                                    });
                                                })
                                                .catch(() =>
                                                    zalozeniLeiApi(zalozeniData).then(respZalozeni => {
                                                        setLevel('lei', {
                                                            data: {
                                                                typzadosti: formData?.typzadosti,
                                                                ...(formData?.subcategory && {
                                                                    subcategory: formData?.subcategory
                                                                }),
                                                                entitycheck: 'N/A',
                                                                language: language,
                                                                spolecnost: {
                                                                    ...(values.legalname && {
                                                                        legalname: values.legalname
                                                                    }),
                                                                    jurisdikce: values.jurisdikce,
                                                                    adresy: [
                                                                        {
                                                                            typadresy: 'LEGALADDRESS',
                                                                            country: values.jurisdikce
                                                                        }
                                                                    ],
                                                                    registrationauthorityid:
                                                                        values.registrationauthorityid,
                                                                    registrationauthorityentityid:
                                                                        values.registrationauthorityentityid,
                                                                    ...(values.otherregistrationauthorityid && {
                                                                        otherregistrationauthorityid:
                                                                            values.otherregistrationauthorityid
                                                                    })
                                                                }
                                                            },
                                                            idVarsymZadosti: respZalozeni.data,
                                                            selectedLei: {
                                                                varsymbol: respZalozeni.data.varsym
                                                            }
                                                        });
                                                        navigateWorkaround(
                                                            `/firma/${ucetResp.iductu}${routePathEnum.LEI_NOVY}`
                                                        );
                                                    })
                                                )
                                                .finally(() => setLoading(false));
                                        } else {
                                            zalozeniLeiApi(zalozeniData)
                                                .then(respZalozeni => {
                                                    setLevel('lei', {
                                                        data: {
                                                            typzadosti: formData?.typzadosti,
                                                            ...(formData?.subcategory && {
                                                                subcategory: formData?.subcategory
                                                            }),
                                                            entitycheck: 'N/A',
                                                            language: language,
                                                            spolecnost: {
                                                                ...(values.legalname && {
                                                                    legalname: values.legalname
                                                                }),
                                                                jurisdikce: values.jurisdikce,
                                                                adresy: [
                                                                    {
                                                                        typadresy: 'LEGALADDRESS',
                                                                        country: values.jurisdikce
                                                                    }
                                                                ],
                                                                registrationauthorityid: values.registrationauthorityid,
                                                                registrationauthorityentityid:
                                                                    values.registrationauthorityentityid,
                                                                ...(values.otherregistrationauthorityid && {
                                                                    otherregistrationauthorityid:
                                                                        values.otherregistrationauthorityid
                                                                })
                                                            }
                                                        },
                                                        idVarsymZadosti: respZalozeni.data,
                                                        selectedLei: {
                                                            varsymbol: respZalozeni.data.varsym
                                                        }
                                                    });
                                                    navigateWorkaround(
                                                        `/firma/${ucetResp.iductu}${routePathEnum.LEI_NOVY}`
                                                    );
                                                })
                                                .finally(() => setLoading(false));
                                        }
                                    });
                                })
                                .catch(() => {
                                    setApiError('UCET_NOVY_ERR');
                                    setLoading(false);
                                });
                        });
                    } else {
                        setLoading(false);
                        setIcoError(
                            resp.stav === 'EXISTUJE'
                                ? resp.stavlei === 'ROZP'
                                    ? 'LEI_ICO_CHECK_ERR_JINY'
                                    : resp.lou
                                      ? resp.lou
                                      : 'LEI_ICO_CHECK_ERR'
                                : 'LEI_ICO_CHECK_ERR_DUPL'
                        );
                    }
                })
                .catch(() => setLoading(false));
        } else {
            novyUcetApi({
                ...values,
                typ: typUctu
            })
                .then(resp => {
                    profilyApi()
                        .then(({ data }) => {
                            const novyUcet = data.sezpristupu!.filter(e => e.iducet === resp.iductu)[0];
                            setLevel('profily', data);
                            setLevel('ucty', data.sezpristupu!);
                            setLevel('aktivniFirma', novyUcet);
                            nastavitUcetApi(novyUcet.iducet)
                                .then(() =>
                                    emitentApi()
                                        .then(rezp => {
                                            setLevel('menuProfile', {
                                                ...rezp,
                                                typemitenta: rezp.typemitenta || EEmitentJe.NA
                                            });
                                            typUctu === 'ADMEMIT'
                                                ? navigateWorkaround(`/firma/${resp.iductu}${routePathEnum.PREHLED}`)
                                                : typUctu === 'PODLT'
                                                  ? zadostPravoCheck().then(response => {
                                                        setLevel('zadostAgenda', response);
                                                        navigateWorkaround(
                                                            `/firma/${resp.iductu}${
                                                                rezp?.amlz !== 'ZADOST'
                                                                    ? profily?.typprihlaseni === 'DLT'
                                                                        ? routePathEnum.CRS_DOTAZNIK_NOVY
                                                                        : routePathEnum.AMLZ_FORMULAR
                                                                    : response?.pristupamlz?.stav === 'CEKA'
                                                                      ? routePathEnum.PREHLED_PRAVNICKA
                                                                      : routePathEnum.AMLZ_ZADOST_O_AGENDU
                                                            }`
                                                        );
                                                    })
                                                  : navigateWorkaround(
                                                        `/firma/${resp.iductu}${routePathEnum.PREHLED_SPRAVA_EMITENTA}`
                                                    );
                                        })
                                        .finally(() => setLoading(false))
                                )
                                .catch(() => {
                                    setLoading(false);
                                });
                        })
                        .catch(() => {
                            setLoading(false);
                        });
                })
                .catch(() => {
                    setLoading(false);
                    setApiError('LEI_CHECK_ERROR2');
                });
        }
    };

    const steps = [
        {
            title: '',
            content: (
                <RozcestnikZalozeni
                    nextStep={next}
                    setFormData={setFormData}
                    setIsGovermentEntity={setIsGovermentEntity}
                />
            )
        },
        {
            title: '',
            content: (
                <Pruvodce
                    prevStep={prev}
                    nextStep={next}
                    pruvodceData={pruvodceData}
                    formDataSpolecnost={formData?.spolecnost}
                    typZadosti={formData?.typzadosti}
                    setPruvodceData={setPruvodceData}
                    setFormData={setFormData}
                    onFinishOuter={onFinish}
                    icoErrorOuter={icoError}
                />
            )
        }
    ];

    return (
        <LoadingWrapper loading={loading} loadingText="NACITANI_DAT">
            <Title text="UCET_NOVY" level={4} />
            {!typSpravceLei && typUctu === 'SPRAVCELEI' && (
                <Row gutter={16}>
                    <Row>
                        <Paragraph
                            text="LEI_NONE_INFO"
                            style={{
                                marginBottom: 8,
                                marginLeft: 12
                            }}
                        />
                    </Row>
                    <ContentCard
                        large={true}
                        onButtonclick={() => {
                            setCurrentStep(0);
                            setTypSpravceLei('NOVE');
                        }}
                        id="leiNovyCard"
                    >
                        <Title
                            level={5}
                            text="LEI_MENU_NOVY"
                            style={{
                                margin: 0
                            }}
                        />
                    </ContentCard>
                    <ContentCard
                        large={true}
                        title=""
                        onButtonclick={() => {
                            setCurrentStep(0);
                            setTypSpravceLei('PRIDELENI');
                        }}
                        id="leiSpravaCard"
                    >
                        <Title
                            level={5}
                            text="MENU_LEI_SPRAVA"
                            style={{
                                margin: 0
                            }}
                        />
                        <InfoTooltip title={'PRIDAT_LEI_KUCTU_TOOLTIP'} />
                    </ContentCard>
                </Row>
            )}
            {typUctu === 'SPRAVCELEI' && typSpravceLei === 'NOVE' && (
                <>
                    <Row gutter={8}>
                        <Col span={24}>{steps[currentStep].content}</Col>
                    </Row>
                </>
            )}
            {typUctu === 'SPRAVCELEI' && typSpravceLei === 'PRIDELENI' && (
                <>
                    <Row gutter={8}>
                        <Col span={24}>
                            <SpravaUctu isNovyUcet={true} setShowSpravaUctu={() => setTypSpravceLei(undefined)} />
                        </Col>
                    </Row>
                </>
            )}
            <FormWrapper onFinish={onFinish} outForm={form}>
                {(typUctu === 'EMITENT' || typUctu === 'ADMEMIT' || typUctu === 'PODLT') && (
                    <>
                        <SelectComponent
                            isFormItem={true}
                            data={cisStaty ? cisStaty : []}
                            name="stat"
                            labelParagraph={'STAT_SIDLA'}
                            onChange={val => {
                                rerender(pv => !pv);
                                form.setFieldsValue({
                                    lei: undefined,
                                    nazev: undefined,
                                    ...(val === 'CY' && {
                                        identifikator: 'ΗΕ'
                                    })
                                });
                            }}
                            disabled={typeof emitServiceResp === 'object'}
                        />
                        <InputComponent
                            label="IDENTIFIKACNI_CISLO"
                            name="identifikator"
                            type="text"
                            rules={icoValidation('stat')}
                            disabled={typeof emitServiceResp === 'object'}
                        />

                        {form.getFieldValue('stat') && emitServiceResp?.lei && (
                            <>
                                <InputComponent
                                    disabled={typeof emitServiceResp === 'object'}
                                    label="LEI_CELE"
                                    name="lei"
                                    maxLength={20}
                                    type="text"
                                    initialValue={emitServiceResp?.lei}
                                    rules={leiValidation()}
                                />
                                {form.getFieldValue('stat') === 'DE' && (
                                    <SelectComponent
                                        isFormItem={true}
                                        name="registrationauthorityid"
                                        labelParagraph="REJSTRIK_KOD"
                                        showOptionValue={true}
                                        optionValueFilter={['RA888888']}
                                        data={[
                                            ...(form.getFieldValue('stat')
                                                ? _getKeyValue_(form.getFieldValue('stat'))(leiRejstrikySelect)
                                                : []),
                                            ...leiRejstrikyPermanent
                                        ]}
                                        onChange={val => {
                                            val === 'RA000161' &&
                                                form.setFieldsValue({
                                                    registrationauthorityentityid: 'ΗΕ'
                                                });
                                            rerender(pv => !pv);
                                        }}
                                        disabled={typeof emitServiceResp === 'object'}
                                    />
                                )}
                                {form.getFieldValue('registrationauthorityid') === 'RA888888' && (
                                    <InputComponent
                                        label="REJSTRIK_NAZEV"
                                        name="otherregistrationauthorityid"
                                        maxLength={100}
                                        disabled={typeof emitServiceResp === 'object'}
                                    />
                                )}
                            </>
                        )}
                        {typeof emitServiceResp === 'object' && (
                            <InputComponent
                                label={
                                    typUctu !== 'PODLT'
                                        ? 'EMP_PODANE_ZADOSTI_NAZEV_SPOLECNOSTI'
                                        : 'EMP_PODANE_ZADOSTI_NAZEV_PO'
                                }
                                name="nazev"
                                initialValue={emitServiceResp.nazev}
                            />
                        )}
                    </>
                )}
                {currentStep === 0 && (
                    <Row className="button-row">
                        {typSpravceLei !== 'PRIDELENI' && typUctu && !isGovermentEntity && (
                            <ButtonComponent
                                buttontext="ZPET"
                                type="default"
                                htmlType="button"
                                id="novyUcetZpetBtn"
                                onClick={() =>
                                    typeof emitServiceResp === 'object'
                                        ? setEmitServiceResp(undefined)
                                        : navigateWorkaround(-1)
                                }
                            />
                        )}
                        {(typUctu === 'EMITENT' || typUctu === 'ADMEMIT' || typUctu === 'PODLT') && (
                            <ButtonComponent
                                buttontext="POKRACOVAT"
                                type="primary"
                                id="novyUcetSubmitBtn"
                                onClick={() => (emitServiceResp ? form.submit() : getNazevEmitent())}
                                extramessage={apiError}
                            />
                        )}
                    </Row>
                )}
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default NovyUcet;
